"use client";

/**
 * SearchCandidatesDisplay - Candidate search results display
 */

import { useState } from "react";
import { LocationOn, Work, PersonSearch } from "@mui/icons-material";
import { Tag, Avatar } from "@/ui";
import { colors, spacing, radius, shadows, textStyles, typography, primitives } from "@/ui/tokens";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import { showAllButtonClass } from "../utils";

interface Candidate {
  id: string | number;
  name?: string;
  email?: string;
  title?: string;
  position_title?: string;
  skills?: (string | { name?: string; value?: string })[];
  location?: string;
  address_address?: string;
  availability?: string;
  available_now?: boolean;
  avatar?: string;
  photo_url?: string;
  /** Profile link (searchCandidates, matchCandidatesToJob) */
  url?: string;
  /** Why the candidate matched */
  reasons?: string[];
  /** Requirements the profile doesn't show (matchCandidatesToJob) */
  gaps?: string[];
  /** Fit score 0-100 and band (matchCandidatesToJob) */
  score?: number;
  fit?: "strong" | "good" | "partial" | "weak";
}

interface SearchOutput {
  success?: boolean;
  status?: string;
  candidates?: Candidate[];
  total?: number;
  totalCount?: number;
  count?: number;
  message?: string;
}

function getSkillName(skill: string | { name?: string; value?: string }): string {
  if (typeof skill === "string") return skill;
  return skill.name || skill.value || "";
}

function CandidateCard({ candidate }: { candidate: Candidate }) {
  const title = candidate.title || candidate.position_title;
  const location = candidate.location || candidate.address_address;
  const avatar = candidate.avatar || candidate.photo_url;
  const skills = candidate.skills?.map(getSkillName).filter(Boolean) || [];
  const availability = candidate.availability
    || (candidate.available_now ? "Available Now" : undefined);
  const fitColor = candidate.fit === "strong" || candidate.fit === "good" ? "success" : "neutral";

  const card = (
    <div
      style={{
        padding: spacing[4],
        border: `1px solid ${colors.border.subtle}`,
        borderRadius: radius.md,
        cursor: "pointer",
        transition: "all 0.2s",
        backgroundColor: colors.canvas.default,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = colors.action.product;
        (e.currentTarget as HTMLDivElement).style.boxShadow = shadows[2];
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = colors.border.subtle;
        (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
      }}
    >
      <div style={{ display: "flex", gap: spacing[3], alignItems: "flex-start" }}>
        <Avatar size="md" name={candidate.name || "Candidate"} src={avatar} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...textStyles.subtitle1, display: "block" }}>
            {candidate.name || `Candidate #${candidate.id}`}
          </span>
          {title && (
            <div style={{ display: "flex", alignItems: "center", gap: spacing[1], marginTop: spacing[1] }}>
              <Work style={{ fontSize: 14, color: colors.text.muted }} />
              <span style={{ ...textStyles.body2, color: colors.text.muted }}>{title}</span>
            </div>
          )}
          {location && (
            <div style={{ display: "flex", alignItems: "center", gap: spacing[1], marginTop: spacing[1] }}>
              <LocationOn style={{ fontSize: 14, color: colors.text.muted }} />
              <span style={{ ...textStyles.body2, color: colors.text.muted }}>{location}</span>
            </div>
          )}
          {skills.length > 0 && (
            <div style={{ display: "flex", gap: spacing[1], flexWrap: "wrap", marginTop: spacing[2] }}>
              {skills.slice(0, 4).map((skill, i) => (
                <Tag key={i} variant="default" size="sm">{skill}</Tag>
              ))}
              {skills.length > 4 && (
                <Tag variant="default" color="neutral" size="sm">+{skills.length - 4}</Tag>
              )}
            </div>
          )}
          {candidate.score !== undefined && (
            <div style={{ marginTop: spacing[2] }}>
              <Tag variant="signal" color={fitColor} size="sm">
                {candidate.score}/100{candidate.fit ? ` · ${candidate.fit} fit` : ""}
              </Tag>
            </div>
          )}
          {!!candidate.reasons?.length && (
            <ul style={{ ...textStyles.caption, color: colors.text.primary, margin: `${spacing[2]}px 0 0`, paddingLeft: spacing[4] }}>
              {candidate.reasons.slice(0, 4).map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          )}
          {!!candidate.gaps?.length && (
            <ul style={{ ...textStyles.caption, color: colors.text.muted, margin: `${spacing[1]}px 0 0`, paddingLeft: spacing[4] }}>
              {candidate.gaps.slice(0, 3).map((gap, i) => (
                <li key={i}>Gap: {gap}</li>
              ))}
            </ul>
          )}
          {availability && (
            <div style={{ marginTop: spacing[2] }}>
              <Tag
                variant="signal"
                color={availability.toLowerCase().includes("available") ? "success" : "neutral"}
                size="sm"
              >
                {availability}
              </Tag>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return candidate.url ? (
    <a href={candidate.url} style={{ color: "inherit", textDecoration: "none", display: "block" }}>
      {card}
    </a>
  ) : (
    card
  );
}

interface StreamingOutput {
  phase?: string;
  progress?: number;
  message?: string;
}

const MAX_VISIBLE = 8;

export function SearchCandidatesDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error, progress } = toolState;

  const result = toolCall.finalOutput as SearchOutput | undefined;
  const candidates = result?.candidates || [];
  const totalCount = result?.total ?? result?.totalCount ?? result?.count ?? candidates.length;
  const visibleCandidates = showAll ? candidates : candidates.slice(0, MAX_VISIBLE);
  const needsTruncation = candidates.length > MAX_VISIBLE;

  // Get streaming phase message from preliminary outputs
  const lastOutput = toolCall.outputs[toolCall.outputs.length - 1];
  const streamingData = lastOutput?.preliminary
    ? (lastOutput.output as StreamingOutput)
    : null;
  const progressLabel =
    streamingData?.message || "Searching candidates...";

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={`${toolCall.toolName === "matchCandidatesToJob" ? "Matched Candidates" : "Search Candidates"}${!isLoading && candidates.length > 0 ? ` (${totalCount})` : ""}`}
        toolName={toolCall.toolName}
        status={status}
        progress={progress}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<PersonSearch style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label={progressLabel} />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && (
          <>
            {/* Results count */}
            <div
              style={{
                paddingLeft: spacing[5],
                paddingRight: spacing[5],
                paddingTop: spacing[3],
                paddingBottom: spacing[3],
                backgroundColor: colors.canvas.card,
                borderBottom: `1px solid ${colors.border.subtle}`,
              }}
            >
              <span style={{ ...textStyles.body2, fontWeight: typography.fontWeight.medium }}>
                {totalCount} {totalCount === 1 ? "candidate" : "candidates"} found
              </span>
              {result?.message && (
                <span style={{ ...textStyles.caption, display: "block", color: colors.text.muted, marginTop: spacing[1] }}>
                  {result.message}
                </span>
              )}
            </div>

            {/* Candidate list */}
            {candidates.length > 0 ? (
              <div style={{ padding: spacing[4] }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: spacing[3],
                  }}
                >
                  {visibleCandidates.map((candidate, i) => (
                    <CandidateCard key={candidate.id || i} candidate={candidate} />
                  ))}
                </div>
                {needsTruncation && !showAll && (
                  <button onClick={() => setShowAll(true)} className={showAllButtonClass}>
                    Show all {candidates.length} candidates
                  </button>
                )}
              </div>
            ) : (
              <ToolEmptyState icon={<PersonSearch />} message="No candidates found matching your criteria" />
            )}
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default SearchCandidatesDisplay;
